import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Video, ArrowUp, ArrowDown, Sparkles, Layers, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import api from "../../api";

// Platform admin component for configuring video call provider order & enable/disable
// for both AI add-on subscribers and base plan clinics.
// Backed by /api/admin/video-settings (restricted to ADMIN_ORG_UID).

const DEFAULT_SETTINGS = {
  ai_addon: [
    { id: "jitsi", label: "Jitsi Meet", badge: "Self-hosted", description: "Self-hosted WebRTC video calling (embedded)", enabled: true },
    { id: "zego", label: "ZegoCloud", badge: "SaaS", description: "SaaS WebRTC fallback provider (embedded)", enabled: true },
    { id: "google_meet", label: "Google Meet", badge: "External", description: "Google Calendar & Meet link", enabled: false },
  ],
  base_tier: [
    { id: "google_meet", label: "Google Meet", badge: "External", description: "Google Calendar & Meet link", enabled: true },
    { id: "jitsi", label: "Jitsi Meet", badge: "Self-hosted", description: "Self-hosted WebRTC video calling (embedded)", enabled: false },
    { id: "zego", label: "ZegoCloud", badge: "SaaS", description: "SaaS WebRTC fallback provider (embedded)", enabled: false },
  ],
  supervision: [
    { id: "jitsi", label: "Jitsi Meet", badge: "Self-hosted", description: "Self-hosted WebRTC video calling (embedded)", enabled: true },
    { id: "zego", label: "ZegoCloud", badge: "SaaS", description: "SaaS WebRTC fallback provider (embedded)", enabled: true },
    { id: "google_meet", label: "Google Meet", badge: "External", description: "Google Calendar & Meet link", enabled: true },
  ],
};

function ProviderTierSection({ title, subtitle, tierKey, badgeText, badgeColor, items, onMove, onToggle }) {
  const enabledItems = items.filter((item) => item.enabled);

  return (
    <div className="flex-1 bg-gray-50/70 border border-gray-200 rounded-xl p-5 flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 text-base">{title}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeColor}`}>
              {badgeText}
            </span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-4">{subtitle}</p>

        {/* Provider List */}
        <div className="space-y-2.5">
          {items.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === items.length - 1;
            const enabledRank = item.enabled ? enabledItems.findIndex((e) => e.id === item.id) + 1 : null;

            return (
              <div
                key={item.id}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-all ${
                  item.enabled
                    ? "bg-white border-gray-200 shadow-xs"
                    : "bg-gray-100/60 border-gray-200/60 opacity-60"
                }`}
              >
                {/* Reorder buttons + Rank badge */}
                <div className="flex items-center gap-2">
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      disabled={isFirst}
                      onClick={() => onMove(tierKey, index, -1)}
                      title="Move up in priority"
                      className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-20 disabled:hover:bg-transparent"
                    >
                      <ArrowUp size={13} />
                    </button>
                    <button
                      type="button"
                      disabled={isLast}
                      onClick={() => onMove(tierKey, index, 1)}
                      title="Move down in priority"
                      className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-20 disabled:hover:bg-transparent"
                    >
                      <ArrowDown size={13} />
                    </button>
                  </div>

                  <div className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                    {index + 1}
                  </div>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800">{item.label}</span>
                    <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                      {item.badge}
                    </span>
                    {item.enabled ? (
                      <span className="text-[11px] text-emerald-600 font-medium">
                        {enabledRank === 1 ? "● Primary" : `● Fallback ${enabledRank - 1}`}
                      </span>
                    ) : (
                      <span className="text-[11px] text-gray-400">Disabled</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{item.description}</p>
                </div>

                {/* Toggle switch */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={item.enabled}
                  aria-label={`Enable or disable ${item.label}`}
                  onClick={() => onToggle(tierKey, item.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
                    item.enabled ? "bg-blue-600" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                      item.enabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fallback Flow Indicator */}
      <div className="mt-4 pt-3 border-t border-gray-200 text-xs">
        <span className="text-gray-500 font-medium">Fallback flow: </span>
        {enabledItems.length > 0 ? (
          <span className="text-gray-700 font-medium">
            {enabledItems.map((e, idx) => (
              <span key={e.id}>
                {idx > 0 && <span className="text-blue-500 mx-1">➔</span>}
                <span className={idx === 0 ? "text-blue-700 font-semibold" : ""}>{e.label}</span>
              </span>
            ))}
          </span>
        ) : (
          <span className="text-red-500 font-semibold inline-flex items-center gap-1">
            <AlertTriangle size={12} /> No provider enabled! At least one provider is required.
          </span>
        )}
      </div>
    </div>
  );
}

export default function VideoCallSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.fetchVideoCallSettings();
      setSettings(res.data);
    } catch (e) {
      // If 403 or network issue, silently ignore or show defaults
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  const handleMove = (tierKey, index, direction) => {
    setSettings((prev) => {
      const list = [...prev[tierKey]];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= list.length) return prev;
      const [moved] = list.splice(index, 1);
      list.splice(targetIndex, 0, moved);
      return { ...prev, [tierKey]: list };
    });
  };

  const handleToggle = (tierKey, providerId) => {
    setSettings((prev) => {
      const list = prev[tierKey].map((item) =>
        item.id === providerId ? { ...item, enabled: !item.enabled } : item
      );
      return { ...prev, [tierKey]: list };
    });
  };

  const handleResetDefaults = () => {
    if (window.confirm("Reset video call providers and order to default settings?")) {
      setSettings(JSON.parse(JSON.stringify(DEFAULT_SETTINGS)));
    }
  };

  const handleSave = async () => {
    const aiEnabled = settings.ai_addon.filter((x) => x.enabled);
    const baseEnabled = settings.base_tier.filter((x) => x.enabled);
    if ((settings.supervision || []).filter((x) => x.enabled).length === 0) {
      toast.error("Please enable at least one video provider for supervision calls.");
      return;
    }

    if (aiEnabled.length === 0) {
      toast.error("Please enable at least one video provider for AI Add-on clinics.");
      return;
    }
    if (baseEnabled.length === 0) {
      toast.error("Please enable at least one video provider for Base Plan clinics.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ai_addon: settings.ai_addon.map((x) => ({ id: x.id, enabled: x.enabled })),
        base_tier: settings.base_tier.map((x) => ({ id: x.id, enabled: x.enabled })),
        supervision: (settings.supervision || DEFAULT_SETTINGS.supervision).map((x) => ({ id: x.id, enabled: x.enabled })),
      };
      const res = await api.updateVideoCallSettings(payload);
      setSettings(res.data);
      toast.success("Video calling settings updated successfully");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to update video call settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) return null;

  const hasInvalidTier =
    settings.ai_addon.filter((x) => x.enabled).length === 0 ||
    settings.base_tier.filter((x) => x.enabled).length === 0 ||
    (settings.supervision || []).filter((x) => x.enabled).length === 0;

  return (
    <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
            <Video size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Online Video Calling Settings</h2>
            <p className="text-sm text-gray-500">
              Configure which tool to use for video calling, enable/disable options, and adjust the fallback order.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResetDefaults}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 rounded-lg border border-gray-200 hover:bg-gray-50 transition"
          >
            <RotateCcw size={13} /> Reset Defaults
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || hasInvalidTier}
            className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition shadow-xs"
          >
            {saving ? "Saving…" : "Save Video Settings"}
          </button>
        </div>
      </div>

      {/* Doctor-patient orders, then clinician-supervisor calls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ProviderTierSection
          title="Clinics with AI Add-On"
          subtitle="Applied to clinics with an active AI add-on subscription (eligible for automated session transcription)."
          tierKey="ai_addon"
          badgeText="AI Add-On Subscribed"
          badgeColor="bg-emerald-50 text-emerald-700 border border-emerald-200/60"
          items={settings.ai_addon}
          onMove={handleMove}
          onToggle={handleToggle}
        />

        <ProviderTierSection
          title="Clinics on Base Plan"
          subtitle="Applied to clinics without the AI add-on pack. First provider in order is tried first."
          tierKey="base_tier"
          badgeText="Base Plan (Non-AI)"
          badgeColor="bg-amber-50 text-amber-700 border border-amber-200/60"
          items={settings.base_tier}
          onMove={handleMove}
          onToggle={handleToggle}
        />

        <ProviderTierSection
          title="Supervision calls"
          subtitle="Clinician and supervisor meetings, for every clinic whatever its plan. No patient on the call and nothing is transcribed."
          tierKey="supervision"
          badgeText="Clinician to supervisor"
          badgeColor="bg-violet-50 text-violet-700 border border-violet-200/60"
          items={settings.supervision || DEFAULT_SETTINGS.supervision}
          onMove={handleMove}
          onToggle={handleToggle}
        />
      </div>

      <p className="mt-4 text-xs text-gray-400">
        Changes take effect immediately for upcoming online consultation joins. Existing meetings already in session retain their pinned provider.
      </p>
    </div>
  );
}
